import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { ArrowDown } from 'lucide-react'
import { SectionHeading } from '@/components/landing/section-heading'
import { DATA_FLOW, TECH_FACTS } from '@/constants/content'

export const TechnologySection: FC = (): ReactNode => {
    const lastIndex = DATA_FLOW.length - 1

    return (
        <section
            id='technik'
            aria-labelledby='technik-title'
            className='relative isolate overflow-hidden bg-ink py-24 text-ink-foreground sm:py-28'
        >
            <Image
                src='/images/beton-textur.jpg'
                alt=''
                aria-hidden='true'
                fill
                sizes='100vw'
                className='-z-10 object-cover opacity-[0.07] mix-blend-luminosity'
            />

            <div
                aria-hidden='true'
                className='pointer-events-none absolute inset-0 -z-10 bg-grid-inverted [mask-image:linear-gradient(to_bottom,black,transparent_80%)]'
            />

            <div className='mx-auto max-w-6xl px-6'>
                <div className='grid gap-12 lg:grid-cols-12 lg:gap-10'>
                    <SectionHeading
                        titleId='technik-title'
                        tone='inverted'
                        eyebrow='Messtechnik'
                        title='Hydratationswärme hält sich an keine Arbeitszeiten.'
                        description='Die kritischen Stunden fallen oft in die Nacht oder aufs Wochenende. ConcreTrack misst durchgehend und warnt sofort, sobald die Kern-Rand-Differenz den vereinbarten Grenzwert erreicht.'
                        className='lg:col-span-7'
                    />

                    <dl className='grid gap-x-8 gap-y-7 sm:grid-cols-2 lg:col-span-5 lg:pt-12'>
                        {TECH_FACTS.map((fact) => (
                            <div key={fact.title} className='border-l border-white/15 pl-4'>
                                <dt className='font-medium text-ink-foreground'>
                                    {fact.title}
                                </dt>

                                <dd className='mt-1.5 text-sm leading-relaxed text-ink-foreground/70'>
                                    {fact.text}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>

                <div className='mt-16 rounded-[1.75rem] border border-white/10 bg-white/[0.035] p-6 sm:p-10'>
                    <p className='font-mono text-[0.7rem] font-medium uppercase tracking-[0.18em] text-ink-foreground/60'>
                        So fließen die Daten
                    </p>

                    <ol className='mt-8 grid gap-10 lg:grid-cols-4 lg:gap-6'>
                        {DATA_FLOW.map((step, index) => {
                            const Icon = step.icon

                            return (
                                <li key={step.title} className='relative flex gap-5 lg:block'>
                                    {index < lastIndex && (
                                        <span
                                            aria-hidden='true'
                                            className='absolute -bottom-9 left-6 top-[3.75rem] w-px border-l border-dashed border-white/25 lg:-right-3 lg:bottom-auto lg:left-[3.75rem] lg:top-6 lg:h-0 lg:w-auto lg:border-l-0 lg:border-t'
                                        >
                                            <span className='flow-dot absolute -left-[3.5px] top-0 size-1.5 rounded-full bg-glow shadow-[0_0_12px_2px] shadow-glow/60 motion-reduce:hidden lg:-top-[3.5px] lg:left-0' />

                                            {step.via && (
                                                <span className='absolute left-1/2 top-[-0.72rem] hidden -translate-x-1/2 whitespace-nowrap rounded-full border border-white/15 bg-ink px-2 py-0.5 font-mono text-[0.6rem] uppercase tracking-[0.14em] text-ink-foreground/75 lg:inline-flex'>
                                                    {step.via}
                                                </span>
                                            )}
                                        </span>
                                    )}

                                    <span className='relative z-10 flex size-12 shrink-0 items-center justify-center rounded-2xl border border-white/15 bg-ink text-glow'>
                                        <Icon className='size-5' aria-hidden='true' />
                                    </span>

                                    <div className='lg:mt-6 lg:pr-4'>
                                        <p className='font-mono text-[0.68rem] text-ink-foreground/50'>
                                            {`0${index + 1}`}
                                        </p>

                                        <h3 className='mt-1 font-medium text-ink-foreground'>
                                            {step.title}
                                        </h3>

                                        <p className='mt-2 text-sm leading-relaxed text-ink-foreground/70'>
                                            {step.text}
                                        </p>

                                        {step.via && (
                                            <p className='mt-3 inline-flex items-center gap-1.5 font-mono text-[0.64rem] uppercase tracking-[0.14em] text-glow lg:hidden'>
                                                <ArrowDown className='size-3.5' aria-hidden='true' />
                                                {step.via}
                                            </p>
                                        )}
                                    </div>
                                </li>
                            )
                        })}
                    </ol>
                </div>
            </div>
        </section>
    )
}
